import Popup from 'reactjs-popup';
import styled, { css, keyframes } from 'styled-components';

const rise = keyframes`
	from { opacity: 0; transform: translateY(8px) scale(0.98); }
	to { opacity: 1; transform: none; }
`;

export const ShortcutsPopup = styled(Popup)`
	&-overlay {
		background: rgba(10, 18, 30, 0.55);
	}
	&-content {
		display: flex;
		flex-direction: column;
		width: min(880px, calc(100vw - 32px)) !important;
		max-height: calc(100vh - 48px);
		max-height: calc(100dvh - 48px);
		padding: 0 !important;
		border-radius: 16px;
		border: 1px solid var(--modalBorderColor);
		background: var(--modalBgColor) !important;
		box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
		overflow: hidden;
		animation: ${rise} 0.18s ease-out;

		@media (prefers-reduced-motion: reduce) {
			animation: none;
		}
	}
`;

export const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 16px 20px;
	border-bottom: 1px solid var(--borderLightColor);
`;

export const Title = styled.h3`
	margin: 0;
	font-family: Inter, sans-serif;
	font-size: 1.05rem;
	font-weight: 600;
	color: var(--textHeadingColor);
`;

export const CloseBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	margin-left: auto;
	width: 30px;
	height: 30px;
	padding: 0;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: var(--defaultIconColor);
	cursor: pointer;

	&:hover,
	&:focus-visible {
		background: var(--hoverBgColor);
		outline: none;
	}
`;

// grey body, white cards; auto-fit so cards fill each row
export const Body = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
	gap: 14px;
	padding: 18px 20px 20px;
	overflow-y: auto;
	overscroll-behavior: contain;
	background: var(--surfaceColor);

	@media (max-width: 540px) {
		gap: 12px;
		padding: 14px;
	}
`;

export const Section = styled.section<{ $wide?: boolean }>`
	padding: 14px 16px 10px;
	border: 1px solid var(--borderLightColor);
	border-radius: 14px;
	background: var(--modalBgColor);
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);

	${({ $wide }) => $wide && css`
		grid-column: 1 / -1;
	`}
`;

export const SectionTitle = styled.h4`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 8px;
	margin: 0 0 6px;
	font-family: Inter, sans-serif;
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: var(--subHeadingColor);

	small {
		font-size: 0.72rem;
		font-weight: 500;
		letter-spacing: 0;
		text-transform: none;
	}
`;

// wide cards flow rows into 2 columns, narrow stay 1
export const Rows = styled.div`
	column-width: 280px;
	column-gap: 32px;
`;

export const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin: 0 -8px;
	padding: 7px 8px;
	border-radius: 8px;
	break-inside: avoid;

	&:hover {
		background: var(--hoverBgColor);
	}
`;

const keysWidth = ({ $compact, $long }: { $compact?: boolean; $long?: boolean }) => {
	if ($compact) return 'auto';
	return $long ? '9.25rem' : '100px';
};

export const Keys = styled.div<{ $compact?: boolean; $long?: boolean }>`
	display: flex;
	align-items: center;
	flex: 0 0 ${keysWidth};
	gap: 4px;
	font-size: 0.7rem;
	color: var(--subHeadingColor);
`;

export const KeyCap = styled.kbd`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	height: 24px;
	padding: 0 7px;
	border: 1px solid var(--controlBorderColor);
	border-bottom-width: 2px;
	border-radius: 6px;
	background: var(--surfaceColor);
	color: var(--textHeadingColor);
	font-family: Inter, sans-serif;
	font-size: 0.75rem;
	font-weight: 600;
	line-height: 1;
`;

export const Label = styled.span`
	min-width: 0;
	font-family: Inter, sans-serif;
	font-size: 0.875rem;
	color: var(--textColor);
`;
